import { DECISION_INPUT_OPTIONS } from "@/api/decision-input-values";
import { TypedFactEditor, hasTypedInputEditor, initialTypedInput, typedInputCanSave } from "./typed-fact-editor";
import { guestCountEditValue, isGuestCountValue } from "../guest-count-value";
import { CATERING_FACT_OPTIONS } from "@/api/catering-values";
import React from "react";
import requirementStyles from "./sol-requirements.module.css";
import compactStyles from "./compact-facts.module.css";
import type { FactReview, FactReviewField } from "../../../../../core/src/review/fact-review-types";
import type { SolRequiredField } from "../../../../../core/src/workflow/sol-requirement-types";
import {
  Button, Card, Dialog, FormField, Input, Select, StatusPill,
} from "@/ds";
import { FACT_TONE, Icon, cx } from "@/lib";
import { cockpit } from "@/api";
import type { Fact, OverrideFactResult, RequestDetail, ThreadTargetPage, WorkflowSwitchPreview, ThreadCommand } from "@/api";
import { useLiveInvalidation } from "@/live/live-provider";
import type { ToastAction } from "@/notifications/toast-queue";
import { useT, type TFunc } from "../../i18n";
import {
  draftContinuationMessageKey,
  factProvenanceItems,
  factSourceDisplay,
  operatorRequestReferenceLabel,
  sourceMessageOrderLabels,
} from "../screen-workspace.logic";
import { isEmptyVal, valToStr } from "../fact-value";
import { Menu, type MenuItem } from "./menu";


type Act = (
  p: Promise<unknown>,
  okTitle: string,
  okMsg?: React.ReactNode | ((result: unknown) => React.ReactNode),
  undo?: ToastAction,
) => Promise<boolean>;
const fieldLabel = (t: TFunc, path: string) => t(`field.${path}`, undefined, t("ws.fieldWithoutLabel"));
// ============================ KONVERSATION ============================
export function ThreadOperations({ messages, requestId, writerActionsBlocked, act, data, isOwner }: {
  data: RequestDetail; isOwner: boolean;
  messages: RequestDetail["messages"];
  requestId: string;
  writerActionsBlocked: boolean;
  act: Act;
}) {
  const t = useT();
  const [op, setOp] = React.useState<null | "merge" | "move" | "new">(null);
  const [target, setTarget] = React.useState("");
  const [msgId, setMsgId] = React.useState("");
  const [targetData, setTargetData] = React.useState<RequestDetail | null>(null);
  const [targetPreview, setTargetPreview] = React.useState<WorkflowSwitchPreview | null>(null);
  const [targetEngine, setTargetEngine] = React.useState("");
  const [targetError, setTargetError] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [offset, setOffset] = React.useState(0);
  const [targets, setTargets] = React.useState<ThreadTargetPage | null>(null);
  const [targetsLoading, setTargetsLoading] = React.useState(false);
  const [targetsError, setTargetsError] = React.useState(false);
  const [retry, setRetry] = React.useState(0);
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const targetReads = React.useRef(new Map<string, { promise: Promise<ThreadTargetPage>; repeat: boolean }>());
  const invalidations = useLiveInvalidation("requests", "workspace");
  React.useEffect(() => {
    const timer = window.setTimeout(() => { setSearch(query); setOffset(0); setTarget(""); }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  React.useEffect(() => {
    if (op !== "merge" && op !== "move") return;
    let active = true;
    setTargetsLoading(true); setTargetsError(false); setTarget("");
    const key = `${requestId}:${search}:${offset}`;
    let read = targetReads.current.get(key);
    if (read) read.repeat = true;
    else {
      read = { repeat: false, promise: Promise.resolve({ items: [], next_offset: null }) };
      const currentRead = read;
      read.promise = (async () => {
        let page: ThreadTargetPage;
        do { currentRead.repeat = false; page = await cockpit.listThreadTargets(requestId, search, offset); }
        while (currentRead.repeat && mounted.current);
        return page;
      })().finally(() => { targetReads.current.delete(key); });
      targetReads.current.set(key, read);
    }
    void read.promise.then(page => { if (active) setTargets(page); })
      .catch(() => { if (active) { setTargets(null); setTargetsError(true); } })
      .finally(() => { if (active) setTargetsLoading(false); });
    return () => { active = false; };
  }, [op, requestId, search, offset, invalidations, retry]);
  React.useEffect(() => {
    let active = true; setTargetData(null); setTargetPreview(null); setTargetError(false); setTargetEngine("");
    if (target) void Promise.all([cockpit.getRequest(target), isOwner ? cockpit.previewWorkflowSwitch(target) : Promise.resolve(null)])
      .then(([next, preview]) => { if (active) { setTargetData(next); setTargetPreview(preview); } })
      .catch(() => { if (active) setTargetError(true); });
    return () => { active = false; };
  }, [target, isOwner, requestId, data.request.current_revision, invalidations]);
  const crossEngine = !!targetData && (targetData.request.engine_pin.engine !== data.request.engine_pin.engine
    || targetData.request.engine_pin.profile_hash !== data.request.engine_pin.profile_hash);
  const selectedTarget = targetPreview?.options.find(o => o.engine === targetEngine);
  const targetReady = op === "new" || !targetsLoading && !targetsError && query === search
    && !!target && !!targetData && (!crossEngine || op === "merge" && isOwner && !!selectedTarget?.available);
  const menuTriggerRef = React.useRef<HTMLButtonElement>(null);
  const inbound = messages.filter((message) => message.direction === "inbound" && message.id);

  React.useEffect(() => {
    if (writerActionsBlocked) setOp(null);
  }, [writerActionsBlocked]);

  const targetOptions = [
    { value: "", label: t("ws.targetRequestPlaceholder") },
    ...(targets?.items ?? []).map((other) => {
      const reference = operatorRequestReferenceLabel(other.reference);
      const otherSubject = (other.subject || t("common.noSubject")).slice(0, 34);
      return {
        value: other.id,
        label: reference ? `${otherSubject} · ${reference}` : otherSubject,
      };
    }),
  ];
  const messageOptions = [
    { value: "", label: t("ws.messagePlaceholder") },
    ...inbound.map((message) => ({
      value: message.id as string,
      label: `${(message.subject || t("common.noSubject")).slice(0, 34)} · ${message.fromAddr || "?"}`,
    })),
  ];
  const open = (mode: "merge" | "move" | "new") => {
    if (writerActionsBlocked) return;
    setTarget("");
    setQuery(""); setSearch(""); setOffset(0); setTargets(null);
    setMsgId(inbound.length === 1 ? inbound[0].id as string : "");
    setOp(mode);
  };
  const run = () => {
    if (writerActionsBlocked) return;
    if (!targetReady) return;
    const command: ThreadCommand = { expected_revisions: { [requestId]: data.request.current_revision,
      ...(targetData ? { [targetData.request.id]: targetData.request.current_revision } : {}) } };
    if (crossEngine && selectedTarget?.profile && targetData) command.workflow_target = { engine: selectedTarget.engine,
      profile_hash: selectedTarget.profile.profile_hash, expected_revision: targetData.request.current_revision,
      expected_engine_epoch: targetData.request.engine_pin.engine_epoch, expected_source_revision: data.request.current_revision,
      idempotency_key: crypto.randomUUID() };
    if (op === "merge" && target) void act(cockpit.mergeInto(requestId, target, command), t("ws.merged"));
    else if (op === "move" && msgId && target) void act(cockpit.moveMessage(msgId, target, command), t("ws.moved"));
    else if (op === "new" && msgId) void act(cockpit.newRequestFromMessage(msgId, command), t("ws.newRequestCreated"));
    setOp(null);
  };
  const menuItems: MenuItem[] = [
    { icon: "merge", label: t("ws.merge"), onClick: () => open("merge"), disabled: writerActionsBlocked },
    { icon: "move", label: t("ws.moveMessage"), onClick: () => open("move"), disabled: writerActionsBlocked || !inbound.length },
    { sep: true },
    { icon: "plus", label: t("ws.newFromMessage"), onClick: () => open("new"), disabled: writerActionsBlocked || !inbound.length },
  ];

  return (
    <>
      <Menu
        align="right"
        triggerRef={menuTriggerRef}
        trigger={<button ref={menuTriggerRef} className="iconbtn" disabled={writerActionsBlocked} aria-label={t("ws.threadOptionsAria")}><Icon name="more" size={18} /></button>}
        items={menuItems}
      />
      <Dialog
        open={op !== null && !writerActionsBlocked}
        onClose={() => setOp(null)}
        finalFocusRef={menuTriggerRef}
        tone="navy"
        icon={<Icon name="merge" size={20} />}
        title={op === "merge" ? t("ws.mergeTitle") : op === "move" ? t("ws.moveMessage") : t("ws.newFromMessage")}
        footer={<>
          <Button variant="ghost" onClick={() => setOp(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={writerActionsBlocked || !targetReady || (op === "merge" ? !target : op === "move" ? !(msgId && target) : !msgId)} onClick={run}>{t("common.run")}</Button>
        </>}
      >
        <div className="stack-sm" style={{ paddingBottom: "var(--space-4)" }}>
          {data.request.engine_pin.engine === "single_agent" && <p>{t("workflow.paidChanges")}</p>}
          {targetError && <p role="alert">{t("workflow.loadFailed")}</p>}
          {crossEngine && <p>{t("workflow.error.engine_target_required")}</p>}
          {crossEngine && isOwner && op === "merge" && targetPreview && <FormField label={t("workflow.target")}>
            <Select value={targetEngine} onChange={e => setTargetEngine(e.target.value)} options={[{ value: "", label: t("workflow.selectTarget") },
              ...targetPreview.options.filter(o => o.available).map(o => ({ value: o.engine, label: t(`workflow.${o.engine}`) }))]} />
            <p>{t("workflow.switchConsequences")}</p>
          </FormField>}
          {(op === "move" || op === "new") && (
            <FormField label={t("ws.message")}>
              <Select value={msgId} options={messageOptions} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setMsgId(event.target.value)} />
            </FormField>
          )}
          {(op === "merge" || op === "move") && (
            <div className="stack-sm">
            <FormField label={t("workflow.searchTargets")}><Input value={query} maxLength={120}
              onChange={e => setQuery(e.target.value)} /></FormField>
            {targetsLoading && <p role="status">{t("workflow.loadingTargets")}</p>}
            {targetsError && <p role="alert">{t("workflow.targetsFailed")} <Button size="sm" onClick={() => setRetry(n => n + 1)}>{t("common.retry")}</Button></p>}
            {!targetsLoading && !targetsError && targets?.items.length === 0 && <p>{t("workflow.noTargets")}</p>}
            <FormField label={t("ws.targetRequest")}>
              <Select value={target} options={targetOptions} disabled={targetsLoading || targetsError || query !== search}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setTarget(event.target.value)} />
            </FormField>
            <div className="row">
              {offset > 0 && <Button size="sm" disabled={targetsLoading} onClick={() => setOffset(n => Math.max(0, n - 30))}>{t("workflow.previousTargets")}</Button>}
              {targets?.next_offset != null && <Button size="sm" disabled={targetsLoading} onClick={() => setOffset(targets.next_offset!)}>{t("workflow.moreTargets")}</Button>}
            </div>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}

// ============================ FAKTENLAGE ============================
/**
 * The Fakten list discloses evidence and actions inside each row. Other callers retain
 * direct evidence navigation and visible tools. Both presentations share the same editors
 * and guarded mutations.
 */
const reviewTaskLabel = (t: TFunc, reason: string) => t(reason === "fact_disagreement"
  ? "solFacts.confirmedQuestion" : `workflow.task.${reason}`);

function FactReviewReasons({ field, previous = false, onOpenTasks }: { field: FactReviewField; previous?: boolean; onOpenTasks?: () => void }) {
  const t = useT();
  const tasks = [...new Map(field.tasks.map(task => [`${task.reason_code}:${task.settled_at_send}`, task])).values()];
  const mixed = tasks.some(task => task.settled_at_send) && tasks.some(task => !task.settled_at_send);
  return tasks.length > 0 ? <div className={compactStyles.rowReview}>
    <ul>{tasks.map(task => <li key={`${task.reason_code}:${task.settled_at_send}`}
      className={!previous && !task.settled_at_send ? compactStyles.noticeBlocking : undefined}>
      {previous ? reviewTaskLabel(t, task.reason_code) : !task.settled_at_send ? t("solFacts.blockingTask", { task: reviewTaskLabel(t, task.reason_code) })
        : <>{mixed && <>{t("solFacts.nonBlocking")}: </>}{reviewTaskLabel(t, task.reason_code)}</>}
    </li>)}</ul>
    {!previous && onOpenTasks && <button type="button" className={compactStyles.noticeTasks} onClick={onOpenTasks}>
      {t("solFacts.openTask")}
    </button>}
  </div> : null;
}

function factPosition(row: HTMLElement) {
  let scroller = row.parentElement;
  while (scroller && !(scroller.scrollHeight > scroller.clientHeight
    && ["auto", "scroll"].includes(getComputedStyle(scroller).overflowY))) scroller = scroller.parentElement;
  scroller ??= document.scrollingElement as HTMLElement;
  return { top: row.getBoundingClientRect().top, scroller, scrollTop: scroller.scrollTop, windowY: window.scrollY };
}

export function FactItem({ f, requestId, requestRevision, writerActionsBlocked, act, onEvidenceSelect, onMarkHover, sourceMessageLabel, onEditingChange, requirement, review, conflict = null, absent = false, customerPanel = false, compact = false, attention, needsReview = false, onOpenTasks, onPanelEditingChange, onClaimEditor, previousAssessment = false, previousRequirement = false, revealRequest, onRevealed }: {
  f: Fact;
  /** Presentation only; the server recognizes the customer.type operation independently. */
  customerPanel?: boolean;
  /** The Fakten list opts in; customer-panel rows keep their existing presentation. */
  compact?: boolean;
  revealRequest?: number;
  onRevealed?: (sequence: number) => void;
  onPanelEditingChange?: (field: string, editing: boolean) => void;
  onClaimEditor?: (field: string) => boolean;
  attention?: FactReviewField;
  previousAssessment?: boolean;
  previousRequirement?: boolean;
  needsReview?: boolean;
  onOpenTasks?: () => void;
  requirement?: SolRequiredField;
  review?: FactReviewField;
  /** The check of the active refused Sol result that named this fact (#2473). */
  conflict?: string | null;
  absent?: boolean;
  requestId: string;
  requestRevision: number;
  messages: RequestDetail["messages"];
  writerActionsBlocked: boolean;
  act: Act;
  onEvidenceSelect?: (f: Fact) => void;
  onMarkHover?: (key: string | null) => void;
  sourceMessageLabel?: string | null;
  onEditingChange?: (field: string, editing: boolean) => void;
}) {
  const t = useT();
  const customerTypeEditor = customerPanel && f.fieldPath === "customer.type";
  // Only the operator-owned check is about the lock. Unlocking frees a locked extracted value;
  // an operator-entered value stays the operator's after unlocking.
  const conflictLocked = review?.conflict_locked ?? (conflict === "operator_owned" && f.isLocked && f.status !== "manually_overridden");
  const editButtonRef = React.useRef<HTMLButtonElement>(null);
  const rowRef = React.useRef<HTMLDivElement>(null);
  const revealedRequest = React.useRef<number | undefined>(undefined);
  const savePosition = React.useRef<ReturnType<typeof factPosition> | null>(null);
  const [reservedHeight, setReservedHeight] = React.useState<number>();
  const savingRef = React.useRef(false);
  const returnFocusRef = React.useRef(false);
  const detailsId = React.useId();
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const expanded = open || compact && editing;
  const sol = Boolean(onEditingChange);
  const [saving, setSaving] = React.useState(false);
  const [saveFailed, setSaveFailed] = React.useState(false);
  const [editRevision, setEditRevision] = React.useState<number | null>(null);
  React.useLayoutEffect(() => {
    if (revealRequest === undefined || revealedRequest.current === revealRequest) return;
    if (!expanded) { setOpen(true); return; }
    revealedRequest.current = revealRequest;
    rowRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
    rowRef.current?.focus({ preventScroll: true });
    onRevealed?.(revealRequest);
  }, [expanded, revealRequest, onRevealed]);
  React.useLayoutEffect(() => {
    const row = rowRef.current, position = savePosition.current;
    if (!row || !position) return;
    // Compensate refreshed notices before paint, but never undo a person's scroll (or native anchoring).
    if (position.scroller.scrollTop === position.scrollTop && window.scrollY === position.windowY)
      position.scroller.scrollTop += row.getBoundingClientRect().top - position.top;
    savePosition.current = saving ? factPosition(row) : null;
  });
  React.useEffect(() => {
    onEditingChange?.(f.fieldPath, editing);
    onPanelEditingChange?.(f.fieldPath, editing);
    return () => { onEditingChange?.(f.fieldPath, false); onPanelEditingChange?.(f.fieldPath, false); };
  }, [editing, f.fieldPath, onEditingChange, onPanelEditingChange]);
  const [value, setValue] = React.useState("");
  const [typedValue, setTypedValue] = React.useState<unknown>(null);
  const [customerTypeEditBasis, setCustomerTypeEditBasis] = React.useState<{
    requestRevision: number; factVersion: number | null;
  } | null>(null);
  const editStale = editing && (sol && editRevision !== requestRevision
    || customerTypeEditor && customerTypeEditBasis !== null
      && (customerTypeEditBasis.requestRevision !== requestRevision
        || customerTypeEditBasis.factVersion !== (f.version ?? null)));
  React.useEffect(() => {
    if (!customerTypeEditor && !compact) return;
    if (editing) {
      const target = document.getElementById(`fact-value-${f.fieldPath}`);
      if (target?.matches("input, select, textarea, button")) target.focus();
      else target?.querySelector<HTMLElement>("input, select, textarea, button")?.focus();
    }
    else if (returnFocusRef.current) {
      returnFocusRef.current = false;
      editButtonRef.current?.focus({ preventScroll: compact });
    }
  }, [compact, customerTypeEditor, editing, f.fieldPath]);
  const finishEditing = (keepHeight = false) => {
    returnFocusRef.current = customerTypeEditor || compact;
    if (!keepHeight) setReservedHeight(undefined);
    setEditing(false);
    setSaveFailed(false);
  };
  const [countKind, setCountKind] = React.useState("");
  const [countUpper, setCountUpper] = React.useState("");
  const [countApproximate, setCountApproximate] = React.useState(false);
  const [countInclusive, setCountInclusive] = React.useState(true);
  const [selectedValues, setSelectedValues] = React.useState<string[]>([]);
  const [reason, setReason] = React.useState("");
  const statusCode = FACT_TONE[f.status] ? f.status : "unknown";
  const tone = review?.tone ?? FACT_TONE[statusCode];
  const stateLabel = review ? review.states.map(state => t(`solFacts.status.${state}`)).join(" · ") : t(`status.fact.${statusCode}`);
  const authorityState = !review && (f.authority?.confirmed ? t("factAuthority.confirmed") : f.isLocked ? t("ws.locked") : "");
  const compactStateLabel = [stateLabel, authorityState].filter(Boolean).join(" · ");
  const summaryState = review
    ? review.states.filter(state => state !== "from_email").map(state => t(`solFacts.status.${state}`)).join(" · ")
    : [statusCode !== "extracted" ? stateLabel : "", authorityState].filter(Boolean).join(" · ");
  const displayedValue = f.fieldPath === "customer.type"
    && (!["extracted", "confirmed", "manually_overridden", "externally_resolved",
      "curated_venue_resolved"].includes(f.status)
      || !(DECISION_INPUT_OPTIONS.customerTypes as readonly unknown[]).includes(f.value)) ? null : f.value;
  const dateValue = compact && ["event.dates", "event.end_date"].includes(f.fieldPath);
  const formatDateValue = (value: unknown) => typeof value === "string"
    ? value.replace(/^(\d{4})-(\d{2})-(\d{2})$/, "$3.$2.$1") : value;
  const structuredValue = displayedValue && typeof displayedValue === "object" && !Array.isArray(displayedValue)
    ? displayedValue as Record<string, unknown> : null;
  const compactVenue = compact && f.fieldPath === "event.venue_name" && structuredValue?.version === 1
    && typeof structuredValue.name === "string";
  const compactWindow = compact && f.fieldPath === "logistics.delivery_window" && structuredValue?.version === 1
    && structuredValue.timezone === "Europe/Berlin" && typeof structuredValue.date === "string"
    && typeof structuredValue.start === "string" && typeof structuredValue.end === "string";
  const displayText = compactVenue ? String(structuredValue.name) : compactWindow
    ? `${formatDateValue(structuredValue.date)}, ${structuredValue.start}${structuredValue.start === structuredValue.end ? "" : `–${structuredValue.end}`}`
    : valToStr(dateValue ? Array.isArray(displayedValue)
    ? displayedValue.map(formatDateValue) : formatDateValue(displayedValue) : displayedValue, t);
  const quote = f.evidenceText?.trim();
  const confidencePct = f.confidence != null ? Math.round(f.confidence * 100) : null;
  const rawOverrideReason = f.override_reason ?? f.overrideReason;
  const provenance = factProvenanceItems({
    ...f,
    source_message_label: sourceMessageLabel,
    override_reason: rawOverrideReason === "external_address_operator_confirmation"
      ? t("ws.confirmedByOperator")
      : rawOverrideReason,
  });
  const source = factSourceDisplay({ ...f, source_message_label: sourceMessageLabel });
  const sourceLabel = source ? source.label ?? t(source.labelKey, undefined, t("ws.sourceUnknown"))
    : compact && f.source_message_id ? sourceMessageLabel ?? t("ws.sourceEmailUnknown") : null;
  const detailStateLabel = sourceLabel && !absent ? summaryState : compactStateLabel;
  const externallyResolved = f.status === "externally_resolved";
  const curatedVenue = f.status === "curated_venue_resolved";
  // Only a live match waits for a person. An address the curated store already held is final, and
  // the operator sees where it came from rather than a request to confirm it (#1642).
  const operatorConfirmableExternalAddress = externallyResolved && f.fieldPath === "event.event_address";
  React.useEffect(() => {
    if (writerActionsBlocked && !sol && !customerTypeEditor) setEditing(false);
  }, [writerActionsBlocked, sol, customerTypeEditor]);

  // Every supported correction submits the server-owned closed value shape.
  const cateringOptions = CATERING_FACT_OPTIONS[f.fieldPath as keyof typeof CATERING_FACT_OPTIONS];
  const typedCount = f.fieldPath === "event.guest_count";
  const countValue = typedCount ? guestCountEditValue(countKind, value, countUpper, countApproximate, countInclusive) : null;
  const typedInput = hasTypedInputEditor(f.fieldPath);
  const typedBoolean = (DECISION_INPUT_OPTIONS.booleanFields as readonly string[]).includes(f.fieldPath);
  const typedNumeric = (DECISION_INPUT_OPTIONS.numericFields as readonly string[]).includes(f.fieldPath);
  const typedDate = ["event.dates", "event.end_date"].includes(f.fieldPath);
  const hasValue = !(absent && typedInput && typedValue === null && !customerTypeEditor)
    && (typedInput ? typedInputCanSave(f.fieldPath, typedValue) : typedCount ? countValue !== null : cateringOptions?.multiple ? selectedValues.length > 0 : value.trim().length > 0);
  const inlineEditable = !["event.guest_count_qualifier", "catering.dietary_profile"].includes(f.fieldPath) && (typedInput || typedCount || Boolean(cateringOptions) || isEmptyVal(f.value) || ["string", "number", "boolean"].includes(typeof f.value));
  const startEditing = () => {
    if (writerActionsBlocked || savingRef.current || !inlineEditable || (sol || customerTypeEditor) && editing) return;
    if (onClaimEditor && !onClaimEditor(f.fieldPath)) return;
    setEditRevision(requestRevision);
    setSaveFailed(false);
    if (f.fieldPath === "customer.type") {
      // Live refreshes must not rebase an in-progress correction onto another writer's fact.
      setCustomerTypeEditBasis({ requestRevision, factVersion: f.version ?? null });
    }
    if (typedInput) setTypedValue(initialTypedInput(f.fieldPath, f.value));
    const initial = Array.isArray(f.value) ? f.value : [];
    setSelectedValues(cateringOptions ? initial.filter((key) => (cateringOptions.values as readonly unknown[]).includes(key)) as string[] : []);
    setValue(cateringOptions && !cateringOptions.multiple
      ? (cateringOptions.values as readonly unknown[]).includes(f.value) ? String(f.value) : ""
      : typedBoolean ? typeof f.value === "boolean" ? String(f.value) : "" : isEmptyVal(f.value) ? "" : String(f.value));
    if (typedCount) {
      const prior = isGuestCountValue(f.value) ? f.value : null;
      setCountKind(prior?.kind ?? "");
      setValue(prior ? String(prior.kind === "range" ? prior.lower : prior.value)
        : typeof f.value === "number" ? String(f.value) : "");
      setCountUpper(prior?.kind === "range" ? String(prior.upper) : "");
      setCountApproximate(prior?.kind === "range" ? prior.approximate : false);
      setCountInclusive(prior && "inclusive" in prior ? prior.inclusive : true);
    }
    setReason("");
    if (compact) setOpen(true);
    setEditing(true);
  };
  const save = async () => {
    if (
      writerActionsBlocked || savingRef.current || editStale
      || !hasValue
      || (f.fieldPath === "customer.type" && !customerTypeEditBasis)
    ) return;
    if (compact && rowRef.current) {
      // Keep subsequent facts still after the editor closes; explicit collapse releases the space.
      setReservedHeight(rowRef.current.getBoundingClientRect().height);
      savePosition.current = factPosition(rowRef.current);
    }
    if (!sol && !customerTypeEditor && !compact) setEditing(false);
    savingRef.current = true;
    setSaving(true); setSaveFailed(false);
    const saved = await act(
      cockpit.overrideFact(requestId, {
        field_path: f.fieldPath,
        value: typedInput ? typedValue : typedCount ? countValue! : cateringOptions?.multiple ? selectedValues : typedBoolean ? value === "true" : typedNumeric ? Number(value) : value,
        ...(!customerTypeEditor && reason.trim() ? { reason: reason.trim() } : {}),
        lock: true,
        expected_request_revision: f.fieldPath === "customer.type"
          ? customerTypeEditBasis!.requestRevision : sol ? editRevision! : requestRevision,
        ...(f.fieldPath === "customer.type"
          ? { expected_fact_version: customerTypeEditBasis!.factVersion } : {}),
      }),
      t("ws.factOverridden"),
      (result) => t(sol ? "workflow.recalculationRequired" : draftContinuationMessageKey(
        (result as OverrideFactResult).draft_status,
      )),
    );
    savingRef.current = false;
    setSaving(false);
    if (saved) finishEditing(true);
    else setSaveFailed(true);
  };
  const runAction = async (command: () => Promise<unknown>, title: string, message?: Parameters<Act>[2]) => {
    if (writerActionsBlocked || savingRef.current || editing) return;
    savingRef.current = true; setSaving(true); setSaveFailed(false);
    try { if (!await act(command(), title, message)) setSaveFailed(true); }
    finally { savingRef.current = false; setSaving(false); }
  };
  const confirmExternalFact = () => {
    if (writerActionsBlocked || !operatorConfirmableExternalAddress) return;
    void runAction(
      () => cockpit.overrideFact(requestId, {
        field_path: f.fieldPath,
        value: f.value,
        reason: t("ws.confirmedByOperator"),
        lock: true,
        confirm_external_address: true,
        expected_request_revision: requestRevision,
      }),
      t("ws.markedConfirmed"),
      (result) => t(sol ? "workflow.recalculationRequired" : draftContinuationMessageKey(
        (result as OverrideFactResult).draft_status,
      )),
    );
  };

  const editControl = (!externallyResolved || operatorConfirmableExternalAddress) && (
    <button
      ref={editButtonRef} type="button" className={compact ? compactStyles.editAction : "iconbtn fact-row__tool"}
      disabled={writerActionsBlocked || saving || !inlineEditable || (sol || customerTypeEditor) && editing}
      aria-label={customerTypeEditor ? `${t("ws.customer.edit")} — ${fieldLabel(t, f.fieldPath)}` : t("ws.correctFact")}
      title={!inlineEditable ? t("ws.factStructuredReadOnly") : customerTypeEditor ? t("ws.customer.edit") : undefined}
      onClick={startEditing}
    ><Icon name="pencil" size={14} />{compact && t("solFacts.correctCompact")}</button>
  );

  const controls = (
    <span className={cx("fact-row__tools", compact && compactStyles.actions)} aria-busy={saving}>
      {!customerTypeEditor && editControl}
      {!f.authority && !absent && !operatorConfirmableExternalAddress && !(f.fieldPath === "customer.type" && f.status === "unknown") && (
        <button
          type="button"
          className={compact ? compactStyles.editAction : cx("iconbtn fact-row__tool", f.isLocked && "fact-row__tool--active")}
          disabled={writerActionsBlocked || saving || editing}
          aria-label={f.isLocked ? t("ws.unlock") : t("ws.lock")}
          onClick={() => {
            if (!writerActionsBlocked) {
              void runAction(() => cockpit.lockFact(requestId, f.fieldPath, !f.isLocked, requestRevision), f.isLocked ? t("ws.unlocked") : t("ws.locked"));
            }
          }}
        ><Icon name={f.isLocked ? "lock" : "unlock"} size={14} />{compact && t(f.isLocked ? "ws.unlock" : "ws.lock")}</button>
      )}
      {f.authority && !absent && <Button size="sm" variant="ghost" title={t("factAuthority.lockHint")}
        iconLeft={compact ? <Icon name="lock" size={14} /> : undefined}
        disabled={writerActionsBlocked || saving || editing || f.authority.confirmed}
        onClick={() => { void runAction(() => cockpit.lockFact(requestId, f.fieldPath, !f.isLocked, requestRevision), t(f.isLocked ? "ws.unlocked" : "ws.locked")); }}
      >{t(f.isLocked ? "ws.unlock" : "ws.lock")}</Button>}
      {f.authority && !absent && <Button size="sm" variant={compact ? "secondary" : "ghost"}
        className={compact ? compactStyles.confirm : undefined}
        iconLeft={compact ? <Icon name="check" size={14} /> : undefined}
        title={t("factAuthority.confirmHint")}
        disabled={writerActionsBlocked || saving || editing || f.version === undefined || !f.authority.confirmed && (f.value === null || ["unknown", "ambiguous", "conflicting"].includes(f.status))}
        onClick={() => { if (f.version !== undefined) void runAction(() => cockpit.confirmFact(requestId, f.fieldPath, !f.authority!.confirmed,
          requestRevision, f.version), t(f.authority!.confirmed ? "factAuthority.withdrawn" : "factAuthority.confirmed")); }}
      >{t(f.authority.confirmed ? "factAuthority.withdraw" : "factAuthority.confirm")}</Button>}
      {!compact && <button
        type="button" className="iconbtn fact-row__tool" aria-expanded={open} aria-controls={detailsId}
        aria-label={t("ws.factDetails")}
        onClick={() => setOpen((v) => !v)}
      ><Icon name={open ? "chevronUp" : "chevronDown"} size={14} /></button>}
      {customerTypeEditor && editControl}
    </span>
  );
  const notices = <>
    {!editing && saving && <p role="status" className={compact ? "sr-only" : undefined}>{t("common.saving")}</p>}
    {!editing && saveFailed && <p role="alert">{t("solFacts.actionFailed")}</p>}
    {f.authority && (compact ? <p className={cx("subtle", compactStyles.authorityHint)}>
      <span aria-hidden={!f.authority.locked || f.authority.confirmed}
        data-visible={f.authority.locked && !f.authority.confirmed || undefined}>{t("factAuthority.lockHint")}</span>
      <span aria-hidden={!f.authority.confirmed}
        data-visible={f.authority.confirmed || undefined}>{t("factAuthority.confirmedHint")}</span>
    </p> : (f.authority.locked || f.authority.confirmed) && <p className="subtle">
      {t(f.authority.confirmed ? "factAuthority.confirmedHint" : "factAuthority.lockHint")}</p>)}
    {absent && !inlineEditable && <p className="subtle">{t("ws.factStructuredReadOnly")}</p>}
    {(externallyResolved || curatedVenue) && (
      <div className="fact-row__external-review" data-testid="external-fact-review">
        <StatusPill
          size="sm"
          tone={curatedVenue ? "success" : "info"}
          icon={<Icon name="external" size={12} />}
          label={review ? stateLabel : t(curatedVenue ? "status.fact.curated_venue_resolved" : "status.fact.externally_resolved")}
        />
        {operatorConfirmableExternalAddress && (
          <Button
            size="sm"
            variant="ghost"
            disabled={writerActionsBlocked || saving || editing}
            iconLeft={<Icon name="checkCircle" size={14} />}
            onClick={confirmExternalFact}
          >{t("ws.confirmExternalFact")}</Button>
        )}
      </div>
    )}
  </>;
  const editor = <>
    {editing && (sol || customerTypeEditor || !writerActionsBlocked) && (
      <div className={customerTypeEditor ? "cust-field__edit" : "fact-row__edit"} data-testid="fact-inline-edit" aria-busy={saving}>
        {editStale && <p role="alert">{t(customerTypeEditor ? "ws.customer.typeEditStale" : "solFacts.editStale")}</p>}
        {editStale && <p className="subtle">{t("solFacts.currentValue")}: <strong>{displayText}</strong></p>}
        {editStale && <Button size="sm" variant="ghost" disabled={saving || writerActionsBlocked}
          onClick={() => {
            setCustomerTypeEditBasis({ requestRevision, factVersion: f.version ?? null });
            setEditRevision(requestRevision);
            setSaveFailed(false);
          }}>{t(customerTypeEditor ? "ws.customer.reapplyType" : "solFacts.reapply")}</Button>}
        {saveFailed && !editStale && <p role="alert">{t(customerTypeEditor ? "ws.customer.typeSaveFailed" : "solFacts.saveFailed")}</p>}
        <fieldset disabled={saving || writerActionsBlocked || editStale} className={requirementStyles.editor}>
        <FormField label={customerTypeEditor ? fieldLabel(t, f.fieldPath) : t("ws.correctedValue")} required={!customerTypeEditor} htmlFor={`fact-value-${f.fieldPath}`}>
          {typedInput ? (
            <TypedFactEditor field={f.fieldPath} value={typedValue} onChange={setTypedValue} id={`fact-value-${f.fieldPath}`} />
          ) : typedCount ? (
            <div className="stack-sm">
              <Select id={`fact-value-${f.fieldPath}`} value={countKind}
                options={[{ value: "", label: t("ws.count.chooseKind") }, ...["exact", "estimate", "range", "lower_bound", "upper_bound"].map((kind) =>
                  ({ value: kind, label: t(`ws.count.${kind}`) }))]}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setCountKind(event.target.value)} />
              <Input size="sm" inputMode="numeric" value={value} aria-label={t(countKind === "range" ? "ws.count.lower" : "ws.count.value")}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value)} />
              {countKind === "range" && <>
                <Input size="sm" inputMode="numeric" value={countUpper} aria-label={t("ws.count.upper")}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCountUpper(event.target.value)} />
                <label className="row" style={{ gap: "var(--space-2)" }}><input type="checkbox" checked={countApproximate}
                  onChange={(event) => setCountApproximate(event.target.checked)} />{t("ws.count.approximateRange")}</label>
              </>}
              {(countKind === "lower_bound" || countKind === "upper_bound") &&
                <label className="row" style={{ gap: "var(--space-2)" }}><input type="checkbox" checked={countInclusive}
                  onChange={(event) => setCountInclusive(event.target.checked)} />{t("ws.count.inclusive")}</label>}
            </div>
          ) : cateringOptions?.multiple ? (
            <fieldset id={`fact-value-${f.fieldPath}`} aria-label={t("ws.correctedValue")}
              style={{ border: 0, padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "var(--space-2)" }}>
              {cateringOptions.values.map((key) => (
                <label key={key} className="row" style={{ gap: "var(--space-2)" }}>
                  <input type="checkbox" checked={selectedValues.includes(key)}
                    onChange={(event) => setSelectedValues((prior) => event.target.checked
                      ? [...prior, key] : prior.filter((value) => value !== key))} />
                  {t(`factValue.${key}`, undefined, key)}
                </label>
              ))}
            </fieldset>
          ) : cateringOptions ? (
            <Select id={`fact-value-${f.fieldPath}`} value={value}
              options={[{ value: "", label: t("common.noInfo") }, ...cateringOptions.values.map((key) =>
                ({ value: key, label: t(`factValue.${key}`, undefined, key) }))]}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setValue(event.target.value)} />
          ) : typedBoolean ? (
            <Select id={`fact-value-${f.fieldPath}`} value={value}
              options={[{ value: "", label: t("common.noInfo") }, { value: "true", label: t("common.yes") }, { value: "false", label: t("common.no") }]}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setValue(event.target.value)} />
          ) : (
            <Input id={`fact-value-${f.fieldPath}`} size="sm" value={value} autoFocus type={typedDate ? "date" : typedNumeric ? "number" : "text"}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)} />
          )}
        </FormField>
        {!customerTypeEditor && <FormField label={t("ws.reasonOptional")} htmlFor={`fact-reason-${f.fieldPath}`}>
          <Input id={`fact-reason-${f.fieldPath}`} size="sm" value={reason} placeholder={t("ws.reasonPlaceholder")}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)} />
        </FormField>}
        </fieldset>
        <div className="row-wrap" style={{ justifyContent: "flex-end", gap: "var(--space-2)" }}>
          <Button size="sm" variant="ghost" disabled={saving} onClick={() => finishEditing()}>{t("common.cancel")}</Button>
          <Button size="sm" variant="primary" disabled={
            !hasValue || saving || writerActionsBlocked || editStale
          }
            iconLeft={<Icon name="lock" size={13} />} onClick={() => void save()}>{t(saving ? "solFacts.saving" : sol ? "solFacts.saveAndLock" : "ws.applyAndLock")}</Button>
        </div>
      </div>
    )}
  </>;
  const evidence = <>
    {expanded && (compact ? <div data-testid="fact-details">
      <div className={compactStyles.sourceMeta}>
        {!absent && sourceLabel && <span className={compactStyles.source}>
          {source?.icon && <Icon name={source.icon} size={14} />}
          {onEvidenceSelect && f.source_message_id ? <button type="button" className="linkbtn"
            aria-label={`${t("ws.review.showEvidence")} — ${sourceLabel}`} onClick={() => onEvidenceSelect(f)}>{sourceLabel}</button> : sourceLabel}
        </span>}
        {detailStateLabel && <span className={compactStyles.detailState} data-tone={tone}>
          {detailStateLabel}
        </span>}
      </div>
      {compactWindow && <p className={compactStyles.timezone}>{t("solFacts.berlinLocalTime")}</p>}
      {quote && <blockquote className={compactStyles.quote}>
        {onEvidenceSelect ? <button type="button" className="linkbtn" aria-label={t("ws.review.showEvidence")}
          onClick={() => onEvidenceSelect(f)}>“{quote}”</button> : `“${quote}”`}
      </blockquote>}
      {(confidencePct != null || provenance.length > 0) && <details className={compactStyles.provenance}>
        <summary>{t("solFacts.provenanceDetails")}</summary>
        <dl className="fact__evidence">
          {confidencePct != null && <><dt>{t("ws.confidence")}</dt><dd>{t("ws.confidencePct", { n: confidencePct })}</dd></>}
          {provenance.map(item => <React.Fragment key={item.key}><dt>{t(item.labelKey)}</dt><dd>{item.value}</dd></React.Fragment>)}
        </dl>
      </details>}
    </div> : (
      <dl id={detailsId} className="fact__evidence fact-row__details" data-testid="fact-details">
        <dt>{t("ws.status")}</dt>
        <dd><StatusPill size="sm" tone={tone} label={stateLabel} /></dd>
        {!absent && sourceLabel && (
          <>
            <dt>{t("ws.sourceFieldLabel")}</dt>
            <dd><span className="fact__source-line">{source?.icon && <Icon name={source.icon} size={14} />}{sourceLabel}</span></dd>
          </>
        )}
        {quote && (
          <>
            <dt>{t("ws.originalFieldLabel")}</dt>
            <dd className="fact__quote">
              {onEvidenceSelect ? (
                <button
                  type="button"
                  className="linkbtn"
                  aria-label={t("ws.review.showEvidence")}
                  onClick={() => onEvidenceSelect(f)}
                >
                  “{quote}”
                </button>
              ) : `“${quote}”`}
            </dd>
          </>
        )}
        {confidencePct != null && (
          <>
            <dt>{t("ws.confidence")}</dt>
            <dd>{t("ws.confidencePct", { n: confidencePct })}</dd>
          </>
        )}
        {provenance.map((item) => (
          <React.Fragment key={item.key}>
            <dt>{t(item.labelKey)}</dt>
            <dd className="neo-mono">{item.value}</dd>
          </React.Fragment>
        ))}
      </dl>
    ))}
  </>;

  return (
    <div ref={rowRef} className={cx("fact fact-row", customerTypeEditor ? "cust-field" : review ? "" : `fact--${f.status}`, conflict && requirementStyles.attention, compact && compactStyles.row)}
      style={compact && expanded && reservedHeight !== undefined ? { minHeight: reservedHeight } : undefined}
      data-expanded={compact ? expanded : undefined}
      data-review-needed={compact && needsReview || undefined}
      onFocus={compact ? event => { if (event.target === event.currentTarget) setOpen(true); } : undefined}
      data-testid="fact-row" data-field={f.fieldPath} data-required-open={requirement ? true : undefined}
      data-conflict={conflict ? true : undefined}
      tabIndex={-1} role={requirement || conflict ? "group" : undefined}
      aria-label={conflict ? `${fieldLabel(t, f.fieldPath)}: ${t("solFacts.conflict")}`
        : requirement ? `${fieldLabel(t, f.fieldPath)}: ${stateLabel}` : undefined}>
      {conflict && <div className={requirementStyles.marker}><Icon name="alert" size={14} />
        <span>{t("solFacts.conflict")} · {t(conflictLocked ? "solFacts.conflictLocked" : "solFacts.conflictCheck")}</span></div>}
      {requirement && !review && !conflict && <div className={requirementStyles.marker}><Icon name="info" size={14} />
        <span>{t("solFacts.status.customer_must_answer")} · {t("solFacts.required")}</span></div>}
      <div className="fact-row__line">
        <button
          type="button"
          className={cx("fact-row__hit", customerTypeEditor && "cust-field__fact-hit")}
          disabled={!compact && absent && (writerActionsBlocked || !inlineEditable)}
          aria-expanded={compact ? expanded : undefined}
          aria-controls={compact ? detailsId : undefined}
          onMouseDown={compact ? event => {
            if (event.button !== 0) return;
            // Safari otherwise focuses the row, opening it before this button's click closes it.
            event.preventDefault();
            event.currentTarget.focus({ preventScroll: true });
          } : undefined}
          onMouseEnter={() => { if (!externallyResolved) onMarkHover?.(f.fieldPath); }}
          onMouseLeave={() => { if (!externallyResolved) onMarkHover?.(null); }}
          onFocus={() => { if (!externallyResolved) onMarkHover?.(f.fieldPath); }}
          onBlur={() => { if (!externallyResolved) onMarkHover?.(null); }}
          onClick={() => {
            if (compact) { if (!editing && !saving) { setReservedHeight(undefined); setOpen(value => !value); } }
            else if (absent) startEditing();
            else if (externallyResolved) setOpen(true);
            else onEvidenceSelect?.(f);
          }}
        >
          {!compact && <span className="fact-row__dot" data-tone={tone} title={stateLabel} aria-hidden="true" />}
          {compact && needsReview && <span className="sr-only">{t("solFacts.needsReview")}, </span>}
          {(!review || compact) && <span className="sr-only">{compact ? compactStateLabel : stateLabel}, </span>}
          <span className="fact-row__content">
            <span className="fact-row__label">{fieldLabel(t, f.fieldPath)}</span>
            {!compact && review && <span className={requirementStyles.rowStates}>{stateLabel}</span>}
            <span className="fact-row__value">{customerTypeEditor && displayedValue === null ? <span className="muted">{t("ws.customer.empty")}</span> : displayText}</span>
            {compact && summaryState && !requirement && !needsReview && (tone === "warning" || tone === "danger") && <span className={compactStyles.state} data-tone={tone} aria-hidden="true">{summaryState}</span>}
          </span>
          {compact && <>
            <span className={compactStyles.origin} data-tone={tone} title={compactStateLabel} aria-hidden="true">
              <Icon name={f.authority?.confirmed ? "checkCircle" : f.isLocked ? "lock" : source?.icon ?? "info"} size={14} />
            </span>
            <Icon name={expanded ? "chevronUp" : "chevronDown"} size={14} aria-hidden="true" />
          </>}
          <span className="sr-only">— {t(compact ? "ws.factDetails" : absent ? customerTypeEditor ? "ws.customer.edit" : "ws.correctFact" : externallyResolved ? "ws.factDetails" : "ws.review.showEvidence")}</span>
        </button>
        {!compact && controls}
      </div>
      {compact ? <div id={detailsId} className={compactStyles.expansion} hidden={!expanded}>
        {review?.previous_uncertainty && <p>{t(`solFacts.previousUncertainty.${review.previous_uncertainty}`)}</p>}
        {previousRequirement && <p>{t("solFacts.previousRequirement")}</p>}
        {attention && <FactReviewReasons field={attention} previous={previousAssessment} onOpenTasks={onOpenTasks} />}
        {evidence}
        {controls}
        {notices}
        {editor}
      </div> : <>{notices}{editor}{evidence}</>}
    </div>
  );
}

// Presentation order uses contract field identities only; values and provenance stay server-owned.
const FACT_REVIEW_GROUPS = [
  { key: "event", titleKey: "ws.factGroupEvent", fields: [
    "event.dates", "event.start_time", "event.program_start_time", "event.guest_arrival_time", "event.end_time", "event.end_date",
    "event.guest_count", "event.guest_count_qualifier", "event.guest_count_deferred", "event.venue_name", "event.event_address",
    "event.event_name", "event.event_type", "event.is_multiday", "event.is_outdoor",
  ] },
  { key: "food", titleKey: "ws.factGroupFood", fields: [
    "catering.types", "catering.food_direction", "catering.purpose", "catering.service_form",
    "catering.course_count", "catering.culinary_style", "catering.sweet_requested",
    "catering.dietary_profile", "catering.dietary_requirements", "catering.dietary_guest_counts",
    "catering.drinks_from_neo", "catering.budget_total_eur", "catering.budget",
  ] },
  { key: "logistics", titleKey: "ws.factGroupLogistics", fields: [
    "logistics.setup_access_time", "logistics.delivery_window", "logistics.ground_level_delivery", "logistics.elevator_available", "logistics.elevator_suitable",
    "logistics.kitchen_available", "logistics.kitchen_equipment", "logistics.seating_type", "logistics.seating_availability",
    "catering.service_staff_required", "catering.tableware_required", "catering.tableware_type",
    "catering.coffee_setup", "catering.buffet_tables_required", "logistics.grill_available_on_site",
    "logistics.grill_requested_from_neo", "logistics.grillmaster_requested", "logistics.cook_requested",
    "logistics.weather_protection_available", "logistics.pavilion_requested", "logistics.hall",
    "logistics.stand_number", "logistics.stand_floors", "logistics.trade_fair_equipment",
  ] },
  { key: "other", titleKey: "ws.factGroupOther", fields: [] },
];
const FACT_REVIEW_ORDER = new Map(FACT_REVIEW_GROUPS.flatMap((group, groupIndex) => (
  group.fields.map((field, order) => [field, { groupIndex, order }] as const)
)));

export function FactsPanel({ requestId, requestRevision, facts, review, assessmentPending = false, assessmentFailed = false, messages, writerActionsBlocked, act, onEvidenceSelect, onMarkHover, onEditingChange, requirements, refused = false, historicalRequirements, conflict, onlyRequired = false, onOnlyRequiredChange, onOpenQuestionDraft, onOpenTasks }: {
  requestId: string;
  requestRevision: number;
  facts: Fact[];
  review?: FactReview | null;
  assessmentPending?: boolean;
  assessmentFailed?: boolean;
  onOpenTasks?: () => void;
  requirements?: SolRequiredField[] | null;
  refused?: boolean;
  historicalRequirements?: import("@/api").WorkflowDetail["historical_requirements"];
  /** The fields and check the active refused Sol result names (#2473). */
  conflict?: { fields: readonly string[]; check: string } | null;
  onlyRequired?: boolean;
  onOnlyRequiredChange?: (value: boolean) => void;
  onOpenQuestionDraft?: () => void;
  onEditingChange?: (field: string, editing: boolean) => void;
  messages: RequestDetail["messages"];
  writerActionsBlocked: boolean;
  act: Act;
  onEvidenceSelect?: (f: Fact) => void;
  onMarkHover?: (key: string | null) => void;
}) {
  const t = useT();
  const headerRef = React.useRef<HTMLDivElement>(null);
  const headerHeight = React.useRef(0);
  React.useLayoutEffect(() => {
    if (!assessmentPending) headerHeight.current = headerRef.current?.getBoundingClientRect().height ?? 0;
  });
  const [reviewSummaryOpen, setReviewSummaryOpen] = React.useState(false);
  const [revealRequest, setRevealRequest] = React.useState<{ field: string; sequence: number }>();
  const revealSequence = React.useRef(0);
  const finishReveal = React.useCallback((sequence: number) => {
    // A later filter/remount must not replay an already completed navigation.
    setRevealRequest(current => current?.sequence === sequence ? undefined : current);
  }, []);
  const [requirementInfoOpen, setRequirementInfoOpen] = React.useState(false);
  const editorOwner = React.useRef<string | null>(null);
  const claimEditor = React.useCallback((field: string) => {
    if (editorOwner.current && editorOwner.current !== field) return false;
    editorOwner.current = field;
    return true;
  }, []);
  const [retainedEditors, setRetainedEditors] = React.useState<ReadonlyMap<string, Fact>>(new Map());
  const reviewed = new Map((review?.fields ?? []).map(field => [field.field_path, field]));
  const previousAssessment = assessmentPending || assessmentFailed || review?.assessment === "previous";
  const previousRequired = new Set(review?.previous_required_fields ?? []);
  const attention = (review?.fields ?? []).filter(field => field.conflict_check || field.tasks.length);
  const attentionByField = new Map(attention.map(field => [field.field_path, field]));
  const required = new Map((requirements ?? []).map(item => [item.field_path, item]));
  const conflicting = new Set(conflict?.fields ?? []);
  const persisted = new Set(facts.map(f => f.fieldPath));
  const rows = [...facts, ...(requirements ?? []).filter(item => !persisted.has(item.field_path))
    .map(item => ({ fieldPath: item.field_path, value: null, status: "unknown", isLocked: false } as Fact))];
  for (const field of reviewed.keys()) if (!rows.some(row => row.fieldPath === field))
    rows.push({ fieldPath: field, value: null, status: "unknown", isLocked: false });
  // A named field without a stored fact still gets a row to highlight.
  for (const field of conflicting) if (!rows.some(row => row.fieldPath === field))
    rows.push({ fieldPath: field, value: null, status: "unknown", isLocked: false });
  if (!rows.some(row => row.fieldPath === "event.event_type"))
    rows.push({ fieldPath: "event.event_type", value: null, status: "unknown", isLocked: false });
  for (const [field, fact] of retainedEditors) if (!rows.some(row => row.fieldPath === field)) rows.push(fact);
  const rowBasis = React.useRef(rows); rowBasis.current = rows;
  const trackEditing = React.useCallback((field: string, editing: boolean) => {
    if (editing) editorOwner.current = field;
    else if (editorOwner.current === field) editorOwner.current = null;
    setRetainedEditors(previous => {
      if (previous.has(field) === editing) return previous;
      const next = new Map(previous), fact = rowBasis.current.find(row => row.fieldPath === field);
      if (editing && fact) next.set(field, fact); else next.delete(field);
      return next;
    });
  }, []);
  const needsReview = (row: Fact) => required.has(row.fieldPath) || conflicting.has(row.fieldPath)
    || attentionByField.has(row.fieldPath) || previousRequired.has(row.fieldPath)
    || Boolean(reviewed.get(row.fieldPath)?.previous_uncertainty)
    || ["ambiguous", "conflicting"].includes(row.status)
    || reviewed.get(row.fieldPath)?.states.some(state => state === "unclear_in_email" || state === "conflicting_in_email");
  const reviewRows = rows.filter(row => needsReview(row) || retainedEditors.has(row.fieldPath));
  const noticeFields = previousAssessment ? rows.filter(needsReview).map(row => row.fieldPath)
    : attention.map(field => field.field_path);
  const filterUnavailable = requirements === null && review?.assessment !== "previous";
  const reviewFilterActive = onlyRequired;
  const visibleRows = reviewFilterActive ? reviewRows : rows;
  const counts = facts.reduce<Record<string, number>>((a, f) => { a[f.status] = (a[f.status] || 0) + 1; return a; }, {});
  const groupId = React.useId();
  const sourceMessageLabels = React.useMemo(() => (
    sourceMessageOrderLabels(messages, (n) => t("ws.sourceEmailMessage", { n }))
  ), [messages, t]);
  const groups = (() => {
    const result = FACT_REVIEW_GROUPS.map((group) => ({ ...group, facts: [] as Fact[] }));
    for (const fact of visibleRows) {
      const groupIndex = FACT_REVIEW_ORDER.get(fact.fieldPath)?.groupIndex ?? result.length - 1;
      result[groupIndex].facts.push(fact);
    }
    for (const group of result) {
      group.facts.sort((a, b) => (
        (FACT_REVIEW_ORDER.get(a.fieldPath)?.order ?? 0) - (FACT_REVIEW_ORDER.get(b.fieldPath)?.order ?? 0)
      ));
    }
    return result.filter((group) => group.facts.length > 0);
  })();

  const revealFact = (field: string) => {
    // Summary links target the one mounted editor, preserving local input and evidence controls.
    if (retainedEditors.size > 0) return;
    setReviewSummaryOpen(false);
    if (!visibleRows.some(row => row.fieldPath === field)) onOnlyRequiredChange?.(false);
    setRevealRequest({ field, sequence: ++revealSequence.current });
  };
  const allQuestionsAsked = Boolean(requirements?.length)
    && requirements?.every(item => reviewed.get(item.field_path)?.question === "asked");
  const hasBlockingAttention = attention.some(field => field.conflict_check || field.tasks.some(task => !task.settled_at_send));
  const hasSettledAttentionTasks = attention.some(field => field.tasks.some(task => task.settled_at_send));
  const requirementsComplete = !previousAssessment && !refused && requirements?.length === 0;
  return (
    <div className={cx("ws-facts-section", compactStyles.panel)}>
      <div ref={headerRef} style={{ minHeight: assessmentPending ? headerHeight.current : undefined }}>
      {previousAssessment && <p role="status" className={compactStyles.assessmentStatus}>
        {t(assessmentFailed ? "solFacts.assessmentFailed" : assessmentPending ? "solFacts.assessmentUpdating" : "solFacts.previousAssessment")}
      </p>}
      {!review && (counts.conflicting || counts.ambiguous) ? <div className="row-wrap">
        {counts.conflicting ? <StatusPill size="sm" tone="danger" label={t("ws.conflictCount", { n: counts.conflicting })} /> : null}
        {counts.ambiguous ? <StatusPill size="sm" tone="warning" label={t("ws.ambiguousCount", { n: counts.ambiguous })} /> : null}
      </div> : null}
      {noticeFields.length > 0 && <details className={compactStyles.reviewNotices} open={reviewSummaryOpen}
        onToggle={event => setReviewSummaryOpen(event.currentTarget.open)}>
        <summary className={compactStyles.noticeHeader}>
          <span><strong>{t(noticeFields.length === 1 ? "solFacts.reviewFactOne" : "solFacts.reviewFacts", { n: noticeFields.length })}</strong>
            {!previousAssessment && <small>{t(hasBlockingAttention ? "solFacts.reviewBlocking" : "solFacts.reviewNonBlocking")}</small>}</span>
          <Icon name="chevronDown" size={14} />
        </summary>
        <div className={compactStyles.noticeBody}>
          {requirementsComplete && <p>{t("solFacts.complete")}</p>}
          {!previousAssessment && hasSettledAttentionTasks && <p>{t("solFacts.noticeMeaning")}</p>}
          <div className={compactStyles.noticeLinks}>
            {noticeFields.map(field => <button key={field} type="button" className={compactStyles.factJump}
              disabled={retainedEditors.size > 0} onClick={() => revealFact(field)}>
              <span>{fieldLabel(t, field)}</span><Icon name="arrowRight" size={14} aria-hidden="true" />
            </button>)}
          </div>
          {requirementsComplete && onOpenQuestionDraft && <button type="button" className={compactStyles.draftLink}
            disabled={retainedEditors.size > 0} onClick={onOpenQuestionDraft}>{t("solFacts.questionDraft")}</button>}
        </div>
      </details>}
      {requirementsComplete ? attention.length === 0 && <section className={compactStyles.requirementsComplete} aria-label={t("solFacts.customerAnswers")}>
        <details className={compactStyles.completionDetails}>
          <summary>{t("solFacts.completeCompact")}<Icon name="info" size={14} /></summary>
          <p>{t("solFacts.complete")}</p>
        </details>
        {onOpenQuestionDraft && <button type="button" className={compactStyles.draftLink} disabled={retainedEditors.size > 0}
          aria-label={t("solFacts.questionDraft")} onClick={onOpenQuestionDraft}>{t("solFacts.draftCompact")}</button>}
      </section> : requirements !== undefined && !previousAssessment && <section className={compactStyles.requirements} aria-labelledby={`${groupId}-customer`}>
        <div className={compactStyles.requirementHeading}>
          <h3 id={`${groupId}-customer`}>{t(!refused && requirements?.length ? "solFacts.compactCount" : "solFacts.customerAnswers", { n: requirements?.length ?? 0 })}</h3>
          {!refused && allQuestionsAsked && <button type="button" className={compactStyles.requirementInfo}
            aria-label={t("solFacts.requirementDetails")} aria-expanded={requirementInfoOpen} aria-controls={`${groupId}-requirement-details`}
            onClick={() => setRequirementInfoOpen(open => !open)}><Icon name="info" size={15} /></button>}
        </div>
        {refused ? <div>
          <p role="status">{t(historicalRequirements ? "solRefusal.historicalRequirements" : "solRefusal.noHistoricalRequirements",
            { revision: historicalRequirements?.request_revision ?? "" })}</p>
          {historicalRequirements && <ul>{historicalRequirements.fields.map(item => <li key={item.field_path}>
            {fieldLabel(t, item.field_path)} · {t(`solFacts.state.${item.state}`)}
          </li>)}</ul>}
          {historicalRequirements?.fields.length === 0 && <p>{t("solRefusal.noPreviousGaps")}</p>}
        </div> : assessmentPending || requirements === null ? <p role="status">{t("solFacts.unavailable")}</p> : !requirements.length ? <p>{t("solFacts.complete")}</p>
          : <>
            <div className={compactStyles.questions}>
              {requirements.map(item => {
                const field = reviewed.get(item.field_path);
                const questionLabel = t(`solFacts.question.${field?.question ?? "no_current_draft"}`);
                return <div key={item.field_path} className={compactStyles.question} data-required-open>
                  <button type="button" className={compactStyles.factJump} disabled={retainedEditors.size > 0}
                    title={questionLabel} onClick={() => revealFact(item.field_path)}>
                    <span>{fieldLabel(t, item.field_path)}</span><Icon name="arrowRight" size={14} aria-hidden="true" />
                  </button>
                  {!allQuestionsAsked && <p>{questionLabel}</p>}
                </div>;
              })}
            </div>
            <p id={`${groupId}-requirement-details`} hidden={allQuestionsAsked && !requirementInfoOpen} className={compactStyles.summaryHint}>{allQuestionsAsked && <>
              {onOpenQuestionDraft ? <button type="button" className={compactStyles.draftLink} disabled={retainedEditors.size > 0}
                aria-label={t("solFacts.questionDraft")} title={t("solFacts.questionDraft")}
                onClick={onOpenQuestionDraft}>{t("solFacts.askedCompact")}</button> : t("solFacts.askedCompact")} </>}
              {t("solFacts.questionsPossible")}
            </p>
          </>}
        {!allQuestionsAsked && onOpenQuestionDraft && <button type="button" className={compactStyles.draftLink}
          disabled={retainedEditors.size > 0} onClick={onOpenQuestionDraft}>{t("solFacts.questionDraft")}</button>}
      </section>}
      </div>
      <div className={compactStyles.toolbar} role="group" aria-label={t("solFacts.filterLabel")}>
        <button type="button" className={compactStyles.filter} aria-pressed={!reviewFilterActive}
          disabled={retainedEditors.size > 0 || !onOnlyRequiredChange} onClick={() => onOnlyRequiredChange?.(false)}>
          {t("solFacts.filterAll")}<span>{rows.length}</span>
        </button>
        <button type="button" className={compactStyles.filter} aria-pressed={reviewFilterActive}
          disabled={retainedEditors.size > 0 || !onOnlyRequiredChange || filterUnavailable} onClick={() => onOnlyRequiredChange?.(true)}>
          {t("solFacts.needsReview")}<span>{filterUnavailable ? "—" : rows.filter(needsReview).length}</span>
        </button>
      </div>
      {reviewFilterActive && reviewRows.length === 0 && !filterUnavailable && <p className="subtle">{t(previousAssessment ? "solFacts.previousNoReviewFacts" : "solFacts.noReviewFacts")}</p>}
      {rows.length === 0 && requirements === undefined && <p className="subtle">{t("ws.noFactsYet")}</p>}
      {groups.map((group) => {
        const contactOnly = group.key === "other" && group.facts.every(f => f.fieldPath.startsWith("customer."));
        return (
        <section key={group.key} className="fact-group" aria-labelledby={`${groupId}-${group.key}`}>
          <div className={compactStyles.groupHeading}>
            <h4 id={`${groupId}-${group.key}`} className="fact-group__title">{t(contactOnly ? "solFacts.contactGroup" : group.titleKey)}</h4>
          </div>
          {group.facts.map((f) => (
            <FactItem
              key={f.fieldPath}
              compact
              revealRequest={revealRequest?.field === f.fieldPath ? revealRequest.sequence : undefined}
              onRevealed={finishReveal}
              f={f}
              onEditingChange={onEditingChange}
              onPanelEditingChange={trackEditing}
              onClaimEditor={claimEditor}
              requirement={!previousAssessment ? required.get(f.fieldPath) : undefined}
              review={reviewed.get(f.fieldPath)}
              attention={attentionByField.get(f.fieldPath)}
              previousAssessment={previousAssessment}
              previousRequirement={previousRequired.has(f.fieldPath) || previousAssessment && required.has(f.fieldPath)}
              needsReview={needsReview(f)}
              onOpenTasks={onOpenTasks}
              conflict={conflicting.has(f.fieldPath) ? conflict!.check : attentionByField.get(f.fieldPath)?.conflict_check ?? null}
              absent={!persisted.has(f.fieldPath)}
              requestId={requestId}
              requestRevision={requestRevision}
              messages={messages}
              writerActionsBlocked={writerActionsBlocked || retainedEditors.size > 0 && !retainedEditors.has(f.fieldPath)}
              act={act}
              onEvidenceSelect={onEvidenceSelect}
              onMarkHover={onMarkHover}
              sourceMessageLabel={f.source_message_id ? sourceMessageLabels.get(f.source_message_id) ?? null : null}
            />
          ))}
        </section>
        );
      })}
    </div>
  );
}
